import { useEffect, useRef, useState } from 'react';
import { Check, HelpCircle } from 'lucide-react';
import type { AuditField, CodedFields } from '../types';
import { useCodeEntry } from '../data/codebookSource';
import { codeTitle } from '../data/codeLabels';
import { sameSet } from '../data/fields';
import { StatusPill } from './parts';

// Presentational atoms for the audit compare screen (HUB-504, S3 — read-only
// frame). Built on @hub/ui + the design-system --ch-* tokens. The A/B slots are
// BLIND: "Result A" and "Result B" are opaque display slots (blue / plum), and
// nothing here maps a slot back to the agent vs the human coder. Colours per the
// brief: Result A = --ch-blue-600, Result B = --ch-plum-500, match = --ch-success,
// differs = --ch-warning. No adjudication controls yet — those land in S4–S7.

export type CompareSlot = 'a' | 'b';

/** The blind slot's display metadata (label + accent). Never names a source. */
export const SLOT_META: Record<CompareSlot, { label: string; tag: string; fg: string; bg: string }> = {
  a: { label: 'Result A', tag: 'A', fg: 'var(--chip-slot-a-fg)', bg: 'var(--chip-slot-a-bg)' },
  b: { label: 'Result B', tag: 'B', fg: 'var(--chip-slot-b-fg)', bg: 'var(--chip-slot-b-bg)' },
};

/**
 * Observe an element's content width. ResizeObserver is polyfilled to a no-op in
 * jsdom, so the `initial` seed is what tests (and the first paint) see — kept wide
 * so the frame defaults to the side-by-side layout. Drives the responsive intent.
 */
export function useElementWidth(initial = 1000): [React.RefCallback<HTMLElement>, number] {
  const [width, setWidth] = useState(initial);
  const roRef = useRef<ResizeObserver | null>(null);
  const ref = (el: HTMLElement | null) => {
    roRef.current?.disconnect();
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setWidth(e.contentRect.width);
    });
    ro.observe(el);
    roRef.current = ro;
  };
  useEffect(() => () => roRef.current?.disconnect(), []);
  return [ref, width];
}

/** Per-slot accent tokens (blue = A, plum = B). `rail`/`fg` are vivid in both
 *  themes; `wash` is a translucent composite so it tints correctly over any
 *  surface (light or dark) — never a fixed light wash that glares on navy. */
export const SLOT_ACCENT: Record<CompareSlot, { rail: string; fg: string; wash: string; washHover: string }> = {
  a: {
    rail: 'var(--ch-blue-500)',
    fg: 'var(--chip-slot-a-fg)',
    wash: 'color-mix(in srgb, var(--ch-blue-500) 12%, transparent)',
    washHover: 'color-mix(in srgb, var(--ch-blue-500) 20%, transparent)',
  },
  b: {
    rail: 'var(--ch-plum-500)',
    fg: 'var(--chip-slot-b-fg)',
    wash: 'color-mix(in srgb, var(--ch-plum-500) 14%, transparent)',
    washHover: 'color-mix(in srgb, var(--ch-plum-500) 22%, transparent)',
  },
};

/**
 * The tinted A/B value row: a subtle slot-coloured wash with a 3px accent left
 * rail (blue for A, plum for B) — the prototype's `.src-row--a` / `.src-row--b`
 * signature. The wash is a translucent composite so it reads on light AND dark
 * surfaces. Hover deepens the wash. Blind-safe: A/B are opaque display slots.
 */
export function SlotRow({ slot, children }: { slot: CompareSlot; children: React.ReactNode }) {
  const a = SLOT_ACCENT[slot];
  return (
    <span
      className="group/slotrow flex items-center gap-2 rounded-[7px] border border-transparent px-2 py-1.5 transition-colors"
      style={{ background: a.wash, borderLeft: `3px solid ${a.rail}` }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = a.washHover;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = a.wash;
      }}
    >
      {children}
    </span>
  );
}

/** The opaque-slot tag (A / B) — blue for A, plum for B. Reveals no provenance. */
export function SrcTag({ slot }: { slot: CompareSlot }) {
  const m = SLOT_META[slot];
  return (
    <span
      className="inline-flex size-[18px] shrink-0 items-center justify-center rounded font-mono text-[10px] font-bold"
      style={{ backgroundColor: m.bg, color: m.fg }}
    >
      {m.tag}
    </span>
  );
}

/** A blue/plum "Result A" / "Result B" column eyebrow. */
export function SlotEyebrow({ slot }: { slot: CompareSlot }) {
  const m = SLOT_META[slot];
  return (
    <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: m.fg }}>
      {m.label}
    </span>
  );
}

/** Per-slot chip tone for a DIFFERING value — blue for A, plum for B, from the
 *  dark-safe --chip-slot-* token pairs (light ink on a navy-tinted wash in dark,
 *  brand -100 wash + -700 ink in light/off-white). Blind-safe: the tone tracks the
 *  A/B DISPLAY slot only, never agent vs coder. */
const SLOT_CHIP: Record<CompareSlot, { bg: string; fg: string; border: string }> = {
  a: { bg: 'var(--chip-slot-a-bg)', fg: 'var(--chip-slot-a-fg)', border: 'var(--chip-slot-a-border)' },
  b: { bg: 'var(--chip-slot-b-bg)', fg: 'var(--chip-slot-b-fg)', border: 'var(--chip-slot-b-border)' },
};

/** A single code chip — mono code + codebook title. Matching values are tinted teal
 *  ("match"); a differing value is tinted by its blind DISPLAY slot (blue = A, plum =
 *  B) so the two sides read as distinct, attributable colours rather than a single
 *  flat amber. Without a `slot` (rare — no slot context) a differing pill falls back
 *  to the amber "differs" tone. When `onOpenCode` is supplied the chip becomes a
 *  button that opens the codebook detail modal (the S4+ adjudication screen). */
export function CodePill({
  code,
  matched,
  slot,
  onOpenCode,
}: {
  code: string;
  matched: boolean;
  /** the blind display slot this value sits in — drives the differing-value colour */
  slot?: CompareSlot;
  onOpenCode?: (code: string) => void;
}) {
  // The SERVER codebook, not the bundled map. acode() read ~130 compiled-in
  // entries and rendered "Uncatalogued code" for everything else, which on a
  // real case was almost every diagnosis.
  const lookup = useCodeEntry(code);
  // Colour model:
  //  - matched → teal-washed chip (code + title both teal ink).
  //  - differing WITH a slot → the chip sits inside a slot-washed row (blue/plum),
  //    so a slot-washed chip would be tint-on-tint and the title read poorly. Use a
  //    clean surface chip instead: the CODE keeps the slot colour (still reads as
  //    A=blue / B=plum), the border is slot-tinted, but the TITLE is neutral ink on
  //    the surface — legible against the coloured row.
  //  - differing without a slot (rare) → amber "differs" wash.
  let pillBg: string, pillBorder: string, codeColor: string, titleColor: string;
  if (matched) {
    pillBg = 'var(--chip-match-bg)';
    pillBorder = 'var(--chip-match-border)';
    codeColor = 'var(--chip-match-fg)';
    titleColor = 'var(--chip-match-fg)';
  } else if (slot) {
    pillBg = 'var(--bg-surface)';
    pillBorder = SLOT_CHIP[slot].border;
    codeColor = SLOT_CHIP[slot].fg;
    titleColor = 'var(--fg-primary)';
  } else {
    pillBg = 'var(--chip-differs-bg)';
    pillBorder = 'var(--chip-differs-border)';
    codeColor = 'var(--chip-differs-fg)';
    titleColor = 'var(--chip-differs-fg)';
  }
  const inner = (
    <>
      {matched && <Check className="size-3 shrink-0 self-center opacity-75" style={{ color: codeColor }} aria-hidden />}
      <span className="whitespace-nowrap font-mono text-[12.5px] font-semibold" style={{ color: codeColor }}>
        {code}
      </span>
      <span className="truncate text-[11px] opacity-90" style={{ color: titleColor }}>
        {codeTitle(lookup)}
      </span>
    </>
  );
  const style = { backgroundColor: pillBg, border: `1px solid ${pillBorder}` };
  if (onOpenCode) {
    return (
      <button
        type="button"
        onClick={() => onOpenCode(code)}
        title={`${codeTitle(lookup)} — open codebook`}
        className="inline-flex max-w-full items-baseline gap-1 rounded-md px-1.5 py-0.5 text-left"
        style={style}
      >
        {inner}
      </button>
    );
  }
  return (
    <span
      className="inline-flex max-w-full items-baseline gap-1 rounded-md px-1.5 py-0.5"
      style={style}
      title={codeTitle(lookup)}
    >
      {inner}
    </span>
  );
}

/** "not billed" / "none coded" placeholder cell. */
export function EmptyValue({ label }: { label: string }) {
  return <span className="text-xs italic text-muted-foreground">{label}</span>;
}

/** Read-only in-field help affordance ported from the prototype's FieldHelp. The
 *  prototype opens the case assistant; here (no chat this slice) it surfaces the
 *  same guidance as a native tooltip, so the read-only frame keeps the affordance. */
export function FieldHelp({ field }: { field: AuditField }) {
  return (
    <span
      role="img"
      aria-label={`Guidance: ${field.label}`}
      title={field.help.tip}
      className="inline-flex size-4 shrink-0 items-center justify-center rounded-full border border-border text-muted-foreground"
    >
      <HelpCircle className="size-2.5" aria-hidden />
    </span>
  );
}

/** One code cell in a multi-field union row: the mono code + codebook title, tinted
 *  teal when shared (a match) or by its blind DISPLAY slot (blue = A, plum = B) when
 *  one-sided / differing, or a "— not coded —" placeholder when this slot doesn't
 *  carry the code. Symmetric in A/B (the slot is a display position, not provenance). */
export function CodeCell({
  code,
  matched,
  slot,
  onOpenCode,
}: {
  code: string | null;
  matched: boolean;
  slot?: CompareSlot;
  onOpenCode?: (code: string) => void;
}) {
  if (!code) {
    return <span className="text-[11px] italic text-muted-foreground">— not coded —</span>;
  }
  return <CodePill code={code} matched={matched} slot={slot} onOpenCode={onOpenCode} />;
}

/** A row in a multi-field comparison: one distinct code, shown in its A slot and/or
 *  B slot. `shared` = both results carry it (a match). Union of A and B, A-first. */
export interface UnionRow {
  code: string;
  a: string | null;
  b: string | null;
  shared: boolean;
}

/** Build the ordered union of a multi-field's A/B codes (A codes first, then the
 *  B-only codes), matching the prototype's MultiFieldCompare union. Symmetric-safe:
 *  a code's `shared` flag is the only match signal — never which slot "owns" it. */
export function unionRows(a: string[] | undefined, b: string[] | undefined): UnionRow[] {
  const aCodes = a ?? [];
  const bCodes = b ?? [];
  const aset = new Set(aCodes);
  const bset = new Set(bCodes);
  const rows: UnionRow[] = [];
  for (const c of aCodes) rows.push({ code: c, a: c, b: bset.has(c) ? c : null, shared: bset.has(c) });
  for (const c of bCodes) if (!aset.has(c)) rows.push({ code: c, a: null, b: c, shared: false });
  return rows;
}

/**
 * One slot's value for a field, rendered read-only. Single fields → one pill
 * (match iff equal to the other slot). Multi fields → the code list, each pill
 * matched iff the other slot also carries that code. Symmetric in A/B, so it
 * never leaks which slot is the agent.
 */
export function CompareValue({
  field,
  slot,
  value,
  other,
  onOpenCode,
  forceMatched,
}: {
  field: AuditField;
  /** the blind display slot this value sits in — drives the differing-value colour */
  slot: CompareSlot;
  value: CodedFields[keyof CodedFields];
  other: CodedFields[keyof CodedFields];
  /** when supplied, code chips open the codebook detail modal */
  onOpenCode?: (code: string) => void;
  /** tint the chip as matched (teal) even when the slots differ — used to reflect a
   *  slot marked "correct" during adjudication */
  forceMatched?: boolean;
}) {
  if (field.multi) {
    const arr = (value as string[]) ?? [];
    const otherSet = new Set((other as string[]) ?? []);
    if (arr.length === 0) return <EmptyValue label="— none —" />;
    return (
      <span className="flex flex-wrap gap-1">
        {arr.map((c) => (
          <CodePill key={c} code={c} matched={otherSet.has(c)} slot={slot} onOpenCode={onOpenCode} />
        ))}
      </span>
    );
  }
  const v = value as string;
  if (!v) return <EmptyValue label="— not billed —" />;
  return (
    <CodePill code={v} matched={forceMatched || v === (other as string)} slot={slot} onOpenCode={onOpenCode} />
  );
}

/** Whether a field's two slots agree (symmetric — reveals no provenance). */
export function slotsMatch(field: AuditField, a: CodedFields, b: CodedFields): boolean {
  const va = a[field.key];
  const vb = b[field.key];
  return field.multi ? sameSet(va as string[], vb as string[]) : va === vb;
}

/** The compact field-level match capsule shown in the upper-right of a field card.
 *  Single → Match / Differs. Multi → Match when every code is shared, else Differs.
 *  A single tone-tinted StatusPill (teal = match, amber = differs), dark-safe and
 *  single-line — the per-code shared / only-A / only-B detail lives in the card body. */
export function FieldStatusBadge({ field, a, b }: { field: AuditField; a: CodedFields; b: CodedFields }) {
  const matched = slotsMatch(field, a, b);
  return matched ? <StatusPill tone="teal" label="Match" /> : <StatusPill tone="amber" label="Differs" />;
}
